/**
 * 📅 Booking Service — Application Layer
 *
 * Orchestrator يربط بين:
 *   - Booking entity
 *   - Calendar (لما حجز يتأكّد، اليوم في الـ Calendar يصير booked)
 *   - Notifications (مستقبلاً)
 *
 * Use Cases للتاجر:
 *   - عرض الحجوزات
 *   - قبول حجز (Confirm)
 *   - رفض حجز (Reject)
 *   - إلغاء حجز
 *   - إتمام حجز (تلقائي بعد التاريخ)
 *
 * Use Cases (للأدمن أو الـ Customer App):
 *   - إنشاء حجز جديد
 */

import { Booking, type BookingSource } from '@domain/entities/Booking';
import {
  BookingRequestStatus,
  type BookingRequestStatusType,
  isActiveBooking,
} from '@domain/value-objects/BookingRequestStatus';
import { bookingRepository } from '@infrastructure/firebase/repositories/FirestoreBookingRepository';
import {
  callMarkContacted,
  callMarkDateAsBooked,
  callCreateManualBooking,
  callRejectBooking,
  callCancelBooking,
} from '@infrastructure/firebase/functions';
import { useAuthStore, isAdminRole } from '@application/stores/auth.store';
import type {
  IBookingRepository,
  BookingFilters,
} from '@domain/repositories/IBookingRepository';
import { NotFoundError, ForbiddenError } from '@domain/errors/DomainErrors';
import { generateBookingDisplayId, formatBookingDisplayId } from '@shared/utils/bookingId';
import { auditLogService } from './AuditLogService';
import { packageService } from './PackageService';

export interface BookingStats {
  total: number;
  pending: number;
  contacted: number;
  completedExternally: number;
  rejected: number;
  cancelled: number;
  bookedElsewhere: number;
  expired: number;
}

class BookingService {
  constructor(
    private repository: IBookingRepository = bookingRepository
  ) {}

  /**
   * 📋 جلب حجوزات حسب الـ role
   *
   * - admin/superAdmin → كل الحجوزات (للإشراف)
   * - vendor → حجوزات قاعاته فقط
   * - customer → حجوزاته الشخصية
   */
  async getMyBookings(filters?: BookingFilters): Promise<Booking[]> {
    const user = useAuthStore.getState().user;
    if (!user) return [];

    // 🛡️ Admin يشوف الكل
    if (isAdminRole(user.role)) {
      if (filters) {
        return this.repository.query(filters);
      }
      return this.getAllBookings();
    }

    if (!user.providerId) return [];
    let result = await this.repository
      .query({ providerId: user.providerId })
      .catch(() => []);

    // فلترة محلية (status) إن طُلبت
    if (filters?.status) {
      const statuses = Array.isArray(filters.status)
        ? filters.status
        : [filters.status];
      result = result.filter((b) => statuses.includes(b.status));
    }
    return result;
  }

  /**
   * 🔴 Live stream — حجوزات المستخدم حسب دوره (تتحدّث تلقائياً)
   *
   * نفس منطق getMyBookings لكن عبر onSnapshot:
   *   - admin → كل الحجوزات
   *   - provider user → stream واحد على providerId القياسي
   *
   * ⚠️ الفلترة بالحالة (status) تُطبَّق محلياً على النتيجة المدمجة.
   *
   * @returns unsubscribe function
   */
  subscribeMyBookings(
    callback: (bookings: Booking[]) => void,
    onError?: (err: Error) => void,
    filters?: BookingFilters
  ): () => void {
    const user = useAuthStore.getState().user;
    if (!user) {
      callback([]);
      return () => {};
    }

    // 🔎 فلترة محلية بالحالة (لو طُلبت)
    const applyStatusFilter = (list: Booking[]): Booking[] => {
      if (!filters?.status) return list;
      const statuses = Array.isArray(filters.status)
        ? filters.status
        : [filters.status];
      return list.filter((b) => statuses.includes(b.status));
    };

    // 🛡️ Admin يشوف الكل
    if (user.role === 'admin' || user.role === 'super_admin') {
      return this.repository.subscribeQuery(
        filters ?? {},
        (list) => callback(applyStatusFilter(list)),
        onError
      );
    }

    if (!user.providerId) {
      callback([]);
      return () => {};
    }
    return this.repository.subscribeQuery(
      { providerId: user.providerId },
      (list) => callback(applyStatusFilter(list)),
      onError
    );
  }

  /**
   * 🌐 كل الحجوزات (للأدمن فقط)
   */
  async getAllBookings(): Promise<Booking[]> {
    return this.repository.query({});
  }

  /**
   * 🔍 حجز واحد
   */
  async getById(bookingId: string): Promise<Booking> {
    const booking = await this.repository.getById(bookingId);
    if (!booking) {
      throw new NotFoundError('الحجز غير موجود', { bookingId });
    }
    return booking;
  }

  /**
   * 📞 المزود تواصل مع العميل (pending → contacted)
   *
   * ⚠️ نموذج lead-gen: هذا لا يحجز التاريخ — مجرد تحديث حالة الطلب.
   *    الحجز الفعلي عبر markDateAsBooked ("تم الحجز").
   *    Cloud Function `markContacted` تستخدم transaction لمنع race conditions.
   */
  async markContacted(bookingId: string): Promise<void> {
    try {
      const result = await callMarkContacted({ bookingId });
      if (!result.data?.success) {
        throw new Error('فشل تحديث الطلب');
      }
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      const code = error.code || '';
      const message = error.message || 'فشل تحديث الطلب';

      if (code.includes('failed-precondition')) throw new Error(message);
      if (code.includes('permission-denied')) {
        throw new Error('لا تملك صلاحية تحديث هذا الطلب');
      }
      if (code.includes('not-found')) throw new Error('الطلب غير موجود');

      throw new Error(message);
    }
  }

  /**
   * 📅 "تم الحجز" — أهم إجراء في النموذج الجديد (lead-gen).
   *
   * يستدعي Cloud Function `markDateAsBooked` التي تنفّذ في batch واحدة:
   *   - الطلب الفائز → completed_externally
   *   - باقي الطلبات النشطة على نفس القاعة+التاريخ → booked_elsewhere
   *   - توفّر التاريخ (booking_dates) → booked
   *
   * ⚠️ winningRequestDocId = مُعرّف *مستند* الطلب (booking.id)، وليس الـ display code.
   *    مرّر externalBooking: true بدلاً منه إذا كان الحجز خارجياً بلا طلب من التطبيق.
   *
   * @returns عدد الطلبات المنافسة التي أُغلقت (closedCount)
   */
  async markDateAsBooked(params: {
    venueId: string;
    eventDate: string;
    timeSlot?: string;
    winningRequestDocId?: string;
    externalBooking?: boolean;
  }): Promise<number> {
    try {
      const payload: {
        venueId: string;
        eventDate: string;
        timeSlot?: string;
        winningRequestDocId?: string;
        externalBooking?: boolean;
      } = { venueId: params.venueId, eventDate: params.eventDate };
      if (params.timeSlot) payload.timeSlot = params.timeSlot;
      if (params.winningRequestDocId) {
        payload.winningRequestDocId = params.winningRequestDocId;
      }
      if (params.externalBooking) payload.externalBooking = true;

      const result = await callMarkDateAsBooked(payload);
      if (!result.data?.success) {
        throw new Error('فشل تأكيد الحجز');
      }
      return result.data.closedCount ?? 0;
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      const code = error.code || '';
      const message = error.message || 'فشل تأكيد الحجز';

      if (code.includes('failed-precondition')) throw new Error(message);
      if (code.includes('invalid-argument')) throw new Error(message);
      if (code.includes('permission-denied')) {
        throw new Error('لا تملك صلاحية تعديل توفّر هذه القاعة');
      }
      if (code.includes('not-found')) throw new Error('القاعة غير موجودة');

      throw new Error(message);
    }
  }

  /**
   * ❌ التاجر يرفض الحجز
   *
   * 🛡️ H-3 fix: يستدعي Cloud Function `rejectBookingRequest` التي تستخدم
   *    Firestore transaction لمنع race conditions بين confirm/reject
   *    من managers متعددين.
   */
  async rejectBookingRequest(bookingId: string, reason: string): Promise<void> {
    try {
      const result = await callRejectBooking({ bookingId, reason });
      if (!result.data?.success) {
        throw new Error('فشل رفض الحجز');
      }
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      const code = error.code || '';
      const message = error.message || 'فشل رفض الحجز';

      if (code.includes('failed-precondition')) {
        throw new Error(message);
      }
      if (code.includes('permission-denied')) {
        throw new Error('لا تملك صلاحية رفض هذا الحجز');
      }
      if (code.includes('not-found')) {
        throw new Error('الحجز غير موجود');
      }
      if (code.includes('invalid-argument')) {
        throw new Error(message);
      }

      throw new Error(message);
    }
  }

  /**
   * 🚫 إلغاء الطلب (من اللوحة) عبر transaction موثوقة.
   */
  async cancelBooking(bookingId: string, reason?: string): Promise<void> {
    const result = await callCancelBooking({
      bookingId,
      ...(reason?.trim() ? { reason: reason.trim() } : {}),
    });
    if (!result.data?.success) throw new Error('فشل إلغاء الحجز');
  }

  /**
   * 📊 إحصائيات الطلبات للمزود
   */
  async getStats(): Promise<BookingStats> {
    const bookings = await this.getMyBookings();
    return this.computeStats(bookings);
  }

  /**
   * 📊 اشتقاق الإحصائيات من قائمة حجوزات (pure — للـ real-time)
   */
  computeStats(bookings: Booking[]): BookingStats {
    const stats: BookingStats = {
      total: bookings.length,
      pending: 0,
      contacted: 0,
      completedExternally: 0,
      rejected: 0,
      cancelled: 0,
      bookedElsewhere: 0,
      expired: 0,
    };

    for (const b of bookings) {
      switch (b.status) {
        case BookingRequestStatus.PENDING:
          stats.pending++;
          break;
        case BookingRequestStatus.CONTACTED:
          stats.contacted++;
          break;
        case BookingRequestStatus.COMPLETED_EXTERNALLY:
          stats.completedExternally++;
          break;
        case BookingRequestStatus.REJECTED:
          stats.rejected++;
          break;
        case BookingRequestStatus.CANCELLED:
          stats.cancelled++;
          break;
        case BookingRequestStatus.BOOKED_ELSEWHERE:
          stats.bookedElsewhere++;
          break;
        case BookingRequestStatus.EXPIRED:
          stats.expired++;
          break;
      }
    }

    return stats;
  }

  /**
   * 🟡 حجوزات تحتاج action (pending فقط)
   */
  async getPendingBookings(): Promise<Booking[]> {
    return this.getMyBookings({ status: BookingRequestStatus.PENDING });
  }

  /**
   * 📅 حجوزات قاعة معيّنة
   */
  async getVenueBookings(venueId: string): Promise<Booking[]> {
    return this.repository.getByVenue(venueId);
  }

  /**
   * 🔢 عدد حجوزات مزوّد معيّن (يُحسب حيّاً من booking_requests)
   *
   *   بديل العدّاد الـ denormalized القديم (provider.totalBookings) الذي
   *   حُذف لأنه لم يكن يُحدَّث أبداً — فكان يعرض 0 دائماً.
   */
  async countByProvider(providerId: string): Promise<number> {
    const bookings = await this.repository.query({ providerId });
    return bookings.length;
  }

  /**
   * ✅ حجوزات نشطة (pending + confirmed) — للتقويم
   */
  async getActiveBookingsForVenue(venueId: string): Promise<Booking[]> {
    const bookings = await this.repository.getByVenue(venueId);
    return bookings.filter((b) => isActiveBooking(b.status));
  }

  // ═══════════════════════════════════════════════════
  // 🛡️ Admin-only methods
  // ═══════════════════════════════════════════════════

  /**
   * ⚡ Force تغيير حالة الحجز (تجاوز State Machine)
   *
   * للأدمن فقط — للحالات الخاصة (دعم العملاء، خلافات)
   */
  async forceStatusChange(
    bookingId: string,
    newStatus: BookingRequestStatusType,
    adminNote: string
  ): Promise<void> {
    const user = useAuthStore.getState().user;
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      throw new ForbiddenError('صلاحيات غير كافية');
    }
    if (!adminNote.trim()) {
      throw new Error('يجب توضيح سبب التغيير');
    }

    const booking = await this.getById(bookingId);
    const oldStatus = booking.status;

    // تجاوز state machine validation — admin override
    const forced = Booking.create({
      ...booking.props,
      status: newStatus,
      vendorNotes: `[ADMIN OVERRIDE] ${adminNote.trim()}`,
      updatedAt: new Date(),
      // حدّث الـ timestamp المناسب
      ...(newStatus === BookingRequestStatus.CONTACTED ? { contactedAt: new Date() } : {}),
      ...(newStatus === BookingRequestStatus.COMPLETED_EXTERNALLY ? { completedExternallyAt: new Date() } : {}),
      ...(newStatus === BookingRequestStatus.REJECTED ? { rejectedAt: new Date() } : {}),
      ...(newStatus === BookingRequestStatus.CANCELLED ? { cancelledAt: new Date() } : {}),
      ...(newStatus === BookingRequestStatus.BOOKED_ELSEWHERE ? { bookedElsewhereAt: new Date() } : {}),
      ...(newStatus === BookingRequestStatus.EXPIRED ? { expiredAt: new Date() } : {}),
    });

    await this.repository.save(forced);

    // 📜 Audit log
    await auditLogService.log({
      action: 'booking.force_status',
      severity: 'warning',
      description: `حالة الحجز تم تغييرها يدوياً من ${oldStatus} إلى ${newStatus}`,
      resourceType: 'booking',
      resourceId: bookingId,
      resourceName: booking.props.venueName,
      metadata: { oldStatus, newStatus, note: adminNote },
    });
  }

  /**
   * 📝 إنشاء حجز يدوي (الأدمن/التاجر نيابة عن عميل)
   *
   * للحجوزات بالهاتف / WhatsApp / حضوري
   *
   * يدعم:
   *   - عميل مسجّل في التطبيق: يربط بـ uid الموجود (الـ booking يظهر في تطبيقه)
   *   - عميل غير مسجّل (تليفوني/حضوري): uid = null (التمييز عبر source)
   */
  async createManualBooking(params: {
    venueId: string;
    venueName: string;
    providerId: string;
    // 👤 Customer (مسجّل أو غير)
    customerUid?: string;            // ✅ لو مسجّل في التطبيق، حدّد uid
    customerName: string;
    customerPhone: string;
    customerEmail?: string;
    customerGender?: 'male' | 'female' | 'other';
    appCustomerId?: string;
    providerCustomerId?: string;
    /** 🆕 (Phase 3) مصدر الحجز */
    bookingSource?: import('@domain/entities/Booking').BookingSource;
    // 📅 Booking
    bookingDate: string;
    guestCount: number;
    guestType: import('@domain/value-objects/PackageEnums').SectionModeValue;
    menGuestCount: number;
    womenGuestCount: number;
    packageInfo: import('@domain/entities/Booking').BookingPackageInfo;
    notes?: string;
  }): Promise<string> {
    const user = useAuthStore.getState().user;
    if (!user) throw new ForbiddenError('يجب تسجيل الدخول');

    const isAdmin = user.role === 'admin' || user.role === 'super_admin';
    // Provider users بصلاحية bookings.write يقدرون يحجزون
    const isProviderUser =
      user.role === 'provider_owner' ||
      user.role === 'provider_manager' ||
      user.role === 'booking_staff';

    const canBook =
      isAdmin ||
      (isProviderUser && user.permissions?.includes('bookings.write'));

    if (!canBook) {
      throw new ForbiddenError('صلاحيات غير كافية');
    }

    // Provider users يحجزون فقط لقاعات مزوّدهم
    if (isProviderUser && params.providerId !== user.providerId) {
      throw new ForbiddenError('يمكن الحجز فقط لقاعات منشأتك');
    }

    if (params.guestType !== params.packageInfo.sectionMode) {
      throw new Error('نوع الضيوف لا يطابق قسم الباقة المختارة');
    }

    // 🛡️ Re-read packages immediately before the write. This prevents a stale
    // modal from creating a venue-only booking after a package was removed or
    // after any identity/capacity field changed.
    const { packages: matchingPackages } =
      await packageService.listMatchingForDate(
        params.venueId,
        params.bookingDate,
        {
          sectionMode: params.packageInfo.sectionMode,
          hasIncludedServices: params.packageInfo.hasIncludedServices,
          guestCount: params.guestCount,
          ...(params.guestType === 'both'
            ? {
                guestCountMen: params.menGuestCount,
                guestCountWomen: params.womenGuestCount,
              }
            : {}),
        }
      );
    const selectedPackage = matchingPackages.find(
      (pkg) => pkg.key === params.packageInfo.key
    );

    if (!selectedPackage) {
      throw new Error(
        'لا يمكن إكمال الحجز: الباقة لم تعد مطابقة للتاريخ والقسم والخدمات وعدد الضيوف'
      );
    }

    const selectedPackageInfo: import('@domain/entities/Booking').BookingPackageInfo = {
      key: selectedPackage.key,
      name: selectedPackage.props.name,
      sectionMode: selectedPackage.props.sectionMode,
      dayType: selectedPackage.props.dayType,
      season: selectedPackage.props.season,
      hasIncludedServices: selectedPackage.props.hasIncludedServices,
      ...(selectedPackage.props.includedServicesText
        ? { includedServicesText: selectedPackage.props.includedServicesText }
        : {}),
      ...(selectedPackage.props.includedServices
        ? { includedServices: selectedPackage.props.includedServices }
        : {}),
      ...(selectedPackage.props.includedServicesMenText
        ? { includedServicesMenText: selectedPackage.props.includedServicesMenText }
        : {}),
      ...(selectedPackage.props.includedServicesMen
        ? { includedServicesMen: selectedPackage.props.includedServicesMen }
        : {}),
      ...(selectedPackage.props.includedServicesWomenText
        ? { includedServicesWomenText: selectedPackage.props.includedServicesWomenText }
        : {}),
      ...(selectedPackage.props.includedServicesWomen
        ? { includedServicesWomen: selectedPackage.props.includedServicesWomen }
        : {}),
    };

    const id = `manual_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const displayId = generateBookingDisplayId();

    // 👤 Customer UID — له قيمة فقط لعميل مسجّل في التطبيق؛ وإلا null.
    //    التمييز بين عميل التطبيق والدشبورد يعتمد على source + وجود uid.
    const customerUid = params.customerUid ?? null;
    const isGuest = !params.customerUid;

    const now = new Date();
    const booking = Booking.create({
      id,
      displayId,
      venueId: params.venueId,
      venueName: params.venueName,
      providerId: params.providerId,
      customer: {
        uid: customerUid,
        name: params.customerName,
        phone: params.customerPhone,
        ...(params.customerEmail ? { email: params.customerEmail } : {}),
        ...(params.customerGender ? { gender: params.customerGender } : {}),
        ...(params.appCustomerId
          ? { appCustomerId: params.appCustomerId }
          : params.customerUid
            ? { appCustomerId: params.customerUid }
            : {}),
        ...(params.providerCustomerId
          ? { providerCustomerId: params.providerCustomerId }
          : {}),
      },
      bookingDate: params.bookingDate,
      guestCount: params.guestCount,
      guestType: params.guestType,
      menGuestCount: params.menGuestCount,
      womenGuestCount: params.womenGuestCount,
      // الحجز اليدوي يمثل اتفاقاً مؤكداً بالفعل، لذلك لا يمر بحالة pending.
      status: BookingRequestStatus.COMPLETED_EXTERNALLY,
      source: params.bookingSource ?? 'dashboard',
      ...(params.notes ? { notes: params.notes } : {}),
      packageInfo: selectedPackageInfo,
      vendorNotes: isGuest
        ? '[MANUAL] طلب يدوي — عميل غير مسجّل'
        : '[MANUAL] طلب يدوي — عميل مسجّل',
      createdAt: now,
      updatedAt: now,
      completedExternallyAt: now,
    });

    // إنشاء الطلب النهائي + حجز التاريخ + إغلاق المنافسين يتم في transaction
    // خادمية واحدة. لا يوجد مستند pending مؤقت يمكن أن تلتقطه الإشعارات.
    const result = await callCreateManualBooking({
      bookingId: id,
      booking: toManualBookingCallableData(booking),
    });
    if (!result.data?.success) {
      throw new Error('تعذّر إنشاء الحجز اليدوي وتأكيد التاريخ');
    }

    await auditLogService.log({
      action: 'booking.manual_create',
      description: `حجز يدوي لـ ${params.customerName} (${isGuest ? 'غير مسجّل' : 'مسجّل'}) في ${params.venueName}`,
      resourceType: 'booking',
      resourceId: id,
      resourceName: params.venueName,
      metadata: {
        customerName: params.customerName,
        customerUid,
        isGuest,
        createdBy: user.role,
      },
    });

    return id;
  }

  /**
   * ⚡ Bulk action — قبول/رفض/إلغاء حجوزات متعددة
   */
  async bulkAction(
    bookingIds: string[],
    action: 'contact' | 'reject' | 'cancel',
    reason?: string
  ): Promise<{ success: number; failed: number }> {
    const user = useAuthStore.getState().user;
    if (!user) throw new ForbiddenError('يجب تسجيل الدخول');

    let success = 0;
    let failed = 0;

    // 🔄 كل إجراء يمر عبر Cloud Function (transaction + يتخطى rules بأمان)
    for (const id of bookingIds) {
      try {
        if (action === 'contact') {
          await this.markContacted(id);
        } else if (action === 'reject') {
          await this.rejectBookingRequest(id, reason ?? 'رفض جماعي');
        } else {
          await this.cancelBooking(id, reason);
        }
        success++;
      } catch {
        failed++;
      }
    }

    await auditLogService.log({
      action: 'data.bulk_action',
      severity: 'info',
      description: `Bulk ${action} لـ ${bookingIds.length} طلب (${success} نجاح، ${failed} فشل)`,
      metadata: { action, total: bookingIds.length, success, failed },
    });

    return { success, failed };
  }

  /**
   * 🌱 إنشاء حجز تجريبي (للتطوير فقط)
   *
   * في الـ production: العميل ينشئ من Flutter app
   */
  async createSeedBooking(params: {
    venueId: string;
    venueName: string;
    providerId: string;
    daysFromNow: number;
    customerName: string;
    customerPhone: string;
    guestCount: number;
    notes?: string;
    packageInfo?: import('@domain/entities/Booking').BookingPackageInfo;
  }): Promise<string> {
    const user = useAuthStore.getState().user;
    if (!user) throw new Error('يجب تسجيل الدخول');

    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + params.daysFromNow);
    targetDate.setHours(0, 0, 0, 0);
    const dateStr = targetDate.toISOString().split('T')[0]!;

    // 🆔 Firestore document ID = شيء random قصير
    const id = `seed_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    // 🏷️ displayId الجميل (HVL-2605-AB1C23)
    const displayId = generateBookingDisplayId();

    const booking = Booking.create({
      id,
      displayId,
      venueId: params.venueId,
      venueName: params.venueName,
      providerId: params.providerId,
      customer: {
        uid: `customer_${Math.random().toString(36).slice(2, 10)}`,
        name: params.customerName,
        phone: params.customerPhone,
      },
      bookingDate: dateStr,
      guestCount: params.guestCount,
      status: BookingRequestStatus.PENDING,
      source: 'seed',
      ...(params.notes ? { notes: params.notes } : {}),
      ...(params.packageInfo ? { packageInfo: params.packageInfo } : {}),
      createdAt: new Date(),
      updatedAt: new Date(),
    });

    await this.repository.save(booking, true); // إنشاء → createdAt = serverTimestamp
    return id;
  }

}

export const bookingService = new BookingService();

function toManualBookingCallableData(
  booking: Booking
): Record<string, unknown> {
  const document = booking.toFirestore();
  return {
    code: document.code,
    venueId: document.venueId,
    venueName: document.venueName,
    providerId: document.providerId,
    customer: document.customer,
    userId: document.userId,
    eventDate: document.eventDate,
    timeSlot: document.timeSlot,
    guestCount: document.guestCount,
    guestType: document.guestType,
    menGuestCount: document.menGuestCount,
    womenGuestCount: document.womenGuestCount,
    source: document.source,
    packageInfo: document.packageInfo,
    searchSnapshot: document.searchSnapshot,
    customerNote: document.customerNote,
    vendorNotes: document.vendorNotes,
  };
}

// ─── Helper: تحويل Booking لعرض UI ───
export interface BookingView {
  id: string;
  /** 🆔 displayId الجميل (HVL-2605-AB1C23) */
  displayId: string;
  venueId: string;
  venueName: string;
  customerName: string;
  customerPhone: string;
  customerGender?: 'male' | 'female' | 'other' | undefined;
  bookingDate: string;
  formattedDate: string;
  guestCount: number;
  status: BookingRequestStatusType;
  /** 📡 مصدر الحجز (للـ badge) */
  source?: BookingSource | undefined;
  daysUntil: number;
  isPast: boolean;
  isUpcoming: boolean;
  packageKey?: string;
  packageDescription?: string;
  notes?: string;
  vendorNotes?: string;
  /** 🔍 لقطة فلاتر البحث */
  searchSnapshot?: import('@domain/entities/Booking').BookingSearchSnapshot | undefined;
  createdAt: string;
}

/**
 * 📊 اشتقاق الإحصائيات من قائمة `BookingView` (للـ UI بعد الفلترة بالوجوه).
 *
 *   نستخدمها في صفحة الحجوزات لحساب عدّادات الشرائح/البطاقات من قائمة
 *   مفلترة (قاعة/زمن/بحث) بدل القائمة الخام، حتى تطابق العدّادات القائمة
 *   الظاهرة. نفس منطق `BookingService.computeStats` لكن على `BookingView`.
 */
export function computeStatsFromViews(views: BookingView[]): BookingStats {
  const stats: BookingStats = {
    total: views.length,
    pending: 0,
    contacted: 0,
    completedExternally: 0,
    rejected: 0,
    cancelled: 0,
    bookedElsewhere: 0,
    expired: 0,
  };

  for (const v of views) {
    switch (v.status) {
      case BookingRequestStatus.PENDING:
        stats.pending++;
        break;
      case BookingRequestStatus.CONTACTED:
        stats.contacted++;
        break;
      case BookingRequestStatus.COMPLETED_EXTERNALLY:
        stats.completedExternally++;
        break;
      case BookingRequestStatus.REJECTED:
        stats.rejected++;
        break;
      case BookingRequestStatus.CANCELLED:
        stats.cancelled++;
        break;
      case BookingRequestStatus.BOOKED_ELSEWHERE:
        stats.bookedElsewhere++;
        break;
      case BookingRequestStatus.EXPIRED:
        stats.expired++;
        break;
    }
  }

  return stats;
}

export function bookingToView(booking: Booking): BookingView {
  const props = booking.props;

  const view: BookingView = {
    id: booking.id,
    displayId: formatBookingDisplayId(booking.id, props.displayId),
    venueId: props.venueId,
    venueName: props.venueName,
    customerName: props.customer.name,
    customerPhone: props.customer.phone,
    bookingDate: props.bookingDate,
    formattedDate: new Date(props.bookingDate + 'T00:00:00').toLocaleDateString(
      'ar',
      { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
    ),
    guestCount: props.guestCount,
    status: booking.status,
    daysUntil: booking.daysUntilBooking,
    isPast: booking.isPast,
    isUpcoming: booking.isUpcoming,
    createdAt: props.createdAt.toLocaleString('ar'),
  };

  if (props.customer.gender) view.customerGender = props.customer.gender;
  if (props.source) view.source = props.source;
  if (props.searchSnapshot) view.searchSnapshot = props.searchSnapshot;
  if (props.packageInfo) {
    view.packageKey = props.packageInfo.key;
    view.packageDescription = describePackage(props.packageInfo);
  }
  if (props.notes) view.notes = props.notes;
  if (props.vendorNotes) view.vendorNotes = props.vendorNotes;

  return view;
}

/**
 * 📝 وصف مختصر للبكج
 */
function describePackage(
  info: import('@domain/entities/Booking').BookingPackageInfo
): string {
  const parts: string[] = [];
  if (info.name?.trim()) parts.push(info.name.trim());

  const sectionLabels: Record<string, string> = {
    both: 'قسمين',
    men: 'رجال',
    women: 'نساء',
  };
  if (info.sectionMode in sectionLabels) {
    parts.push(sectionLabels[info.sectionMode]!);
  }

  const dayLabels: Record<string, string> = {
    weekday: 'وسط الأسبوع',
    weekend: 'ويكند',
    all: 'كل الأيام',
  };
  if (info.dayType in dayLabels && info.dayType !== 'all') {
    parts.push(dayLabels[info.dayType]!);
  }

  const seasonLabels: Record<string, string> = {
    school: 'موسم المدارس',
    summer: 'الصيف',
    holiday: 'الإجازات',
    eid: 'الأعياد',
    all: 'كل المواسم',
  };
  if (info.season in seasonLabels && info.season !== 'all') {
    parts.push(seasonLabels[info.season]!);
  }

  if (info.hasIncludedServices) parts.push('شامل خدمات');
  else parts.push('بدون خدمات');

  return parts.join(' · ');
}
